import WidthDetector from '@atlaskit/width-detector';
import { when } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import PageContentLoaded from '../../apdex/PageContentLoaded';
import { JoinSiteExperimentConsumerProps, withJoinSiteExperiment } from '../../experiments/join-site';
import { AnalyticsConsumerProps, withAnalytics } from '../../hoc/withAnalytics';
import { PageContent } from '../../model/PageContent';
import Store from '../../store';
import BlanketStore, { BlanketBody } from '../../store/blanketStore';
import FeatureFlagsStore from '../../store/featureFlagsStore';
import ProductStore from '../../store/productStore';
import UIStore from '../../store/uiStore';

import CardRow from './CardRow';
import LinkCard from './LinkCard';
import { adminLink, getHelpfulLinks } from './LinkCard/helpfulLinks';
import ShowAllButton from './ShowAllButton';
import { getVisibleProductsCount } from './util';

type Props = {
  blanketStore: BlanketStore;
  containerWidth: number;
  featureFlagsStore: FeatureFlagsStore;
  productStore: ProductStore;
  uiStore: UIStore;
} & JoinSiteExperimentConsumerProps;

@observer
class Products extends React.Component<Props & AnalyticsConsumerProps> {
  componentDidMount() {
    when(
      () => this.props.productStore.hasLoaded,
      () => {
        const { isUserAdmin, joinableProducts, availableProducts } = this.props.productStore;
        void this.props.analytics.track('yourProducts', 'viewed', {
          count: availableProducts.length,
          isJoinSiteExperimentEnabled: this.props.joinSiteExperiment.isVariation,
          isUserAdmin,
          joinableCount: joinableProducts.length,
        });
      },
    );
  }

  render() {
    const { containerWidth, joinSiteExperiment } = this.props;
    const { flags } = this.props.featureFlagsStore;
    const { isUserAdmin, hasLoaded, availableProducts, joinableProducts } = this.props.productStore;
    const { mobileMode } = this.props.uiStore;

    const links = getHelpfulLinks();
    const maxCardsPerRow = getVisibleProductsCount(containerWidth, mobileMode);

    if (!hasLoaded || (availableProducts.length === 0 && joinableProducts.length === 0)) {
      return null;
    }

    const numProductsAndLinks = availableProducts.length + links.length;
    let canExpand = maxCardsPerRow < numProductsAndLinks;

    if (joinSiteExperiment.isVariation && availableProducts.length < maxCardsPerRow) {
      // Need to recalculate in variation because we don't show links, but do show joinable.
      canExpand = maxCardsPerRow < availableProducts.length + joinableProducts.length;
      const joinableProductsToAdd = joinableProducts.slice(0, maxCardsPerRow - availableProducts.length);
      return (
        <div>
          <PageContentLoaded pageContent={PageContent.PRODUCTS} />
          <CardRow
            action={this.renderActions(canExpand)}
            header={<FormattedMessage {...messages.productsHeader} />}
            products={availableProducts}
            joinableProducts={joinableProductsToAdd}
            links={links}
            fixedCard={this.renderAdminCard(isUserAdmin)}
            maxCardsPerRow={maxCardsPerRow}
            singleRow
          />
        </div>
      );
    }

    if (flags['uchi.show-joinable-sites'] && availableProducts.length === 0 && joinableProducts.length > 0) {
      const canExpandJoinable = maxCardsPerRow < joinableProducts.length;

      return (
        <div>
          <PageContentLoaded pageContent={PageContent.PRODUCTS} />
          <CardRow
            action={this.renderActions(canExpandJoinable)}
            header={<FormattedMessage {...messages.joinableHeader} />}
            joinable
            products={joinableProducts}
            maxCardsPerRow={maxCardsPerRow}
            singleRow
          />
          <CardRow
            header={<FormattedMessage {...messages.helpfulLinksHeader} />}
            links={links}
            maxCardsPerRow={maxCardsPerRow}
            singleRow
          />
        </div>
      );
    }

    return (
      <div>
        <PageContentLoaded pageContent={PageContent.PRODUCTS} />
        <CardRow
          action={this.renderActions(canExpand)}
          header={<FormattedMessage {...messages.productsHeader} />}
          products={availableProducts}
          links={links}
          fixedCard={this.renderAdminCard(isUserAdmin)}
          maxCardsPerRow={maxCardsPerRow}
          singleRow
        />
      </div>
    );
  }

  private renderActions = (canExpand: boolean) => {
    if (canExpand) {
      return (
        <ShowAllButton
          key="show-all-button"
          onClick={() => {
            this.props.blanketStore.showBlanket(BlanketBody.Products);
          }}
        />
      );
    }
  };

  private renderAdminCard = (isUserAdmin: boolean) => {
    if (isUserAdmin) {
      return <LinkCard key="admin-link" {...adminLink} />;
    }
  };
}

export const messages = defineMessages({
  helpfulLinksHeader: {
    defaultMessage: 'Helpful links',
    description: 'Helpful links header',
    id: 'uchi.helpfulLinksHeaderJoinable',
  },
  joinableHeader: {
    defaultMessage: 'Start using these products',
    description: 'A list of products the user can join',
    id: 'uchi.joinableHeader',
  },
  productsHeader: {
    defaultMessage: 'Your products',
    description: 'Products header',
    id: 'uchi.productsHeader',
  },
});

const InjectedProducts = inject(({ blanketStore, featureFlagsStore, productStore, uiStore }: Store) => ({
  blanketStore,
  featureFlagsStore,
  productStore,
  uiStore,
}))(withAnalytics(withJoinSiteExperiment(Products)));

export default () => (
  <WidthDetector>{(width?: number) => <InjectedProducts containerWidth={width || 0} />}</WidthDetector>
);
